import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";

import { useCampaignContext } from "../context/CampaignContext";

import type {
  NavigatorFilters,
  NavigatorLead,
  SelectedNavigatorFilters,
} from "../types";

async function getLeadsRequest(
  workspaceId: string,
  campaignId: string,
  selectedFilters: SelectedNavigatorFilters,
  keywords: string,
  pageSize: number,
): Promise<{
  filters: NavigatorFilters;
  leads: NavigatorLead[];
  totalCount: number;
  isLimitExceeded?: boolean;
}> {
  const {
    filters,
    people,
    paging,
    limit_exceeded: isLimitExceeded,
  } = await post(
    `workspaces/${workspaceId}/campaigns/${campaignId}/sales-navigator/search?count=${pageSize}&start=0`,
    {
      filters: selectedFilters,
      keywords: keywords || undefined,
    },
  );
  return { filters, leads: people, totalCount: paging.total, isLimitExceeded };
}
export default function useNavigatorLeads({
  selectedFilters,
  keywords,
  pageSize,
}: {
  selectedFilters: SelectedNavigatorFilters;
  keywords: string;
  pageSize: number;
}) {
  const { id: campaignId } = useCampaignContext();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { campaignsKeys } = getQueryKeys(workspaceId);

  const {
    data,
    isFetching: isLoadingSalesNavigatorLeads,
    error: navigatorLeadsError,
    refetch: refetchNavigatorLeads,
  } = useQuery({
    queryKey: campaignsKeys.navigatorLeads(
      campaignId,
      keywords,
      selectedFilters,
    ),
    enabled: !!selectedFilters.length || !!keywords,
    queryFn: () =>
      getLeadsRequest(
        workspaceId,
        campaignId,
        selectedFilters,
        keywords,
        pageSize,
      ),
  });

  return {
    isLoadingSalesNavigatorLeads,
    filters: data?.filters,
    leads: data?.leads,
    totalCount: data?.totalCount,
    isLimitExceeded: data?.isLimitExceeded,
    navigatorLeadsError,
    refetchNavigatorLeads,
  };
}
